import { useCallback, useMemo, useState } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';
import { DEFAULT_GAS_CHANNEL } from '../../../constants';
import { KEY_PHRASES_OPERATION_NAME, KeyPhrasesQuery } from '../gql/keyphrases-query';
import { onShownAutoDefinitionHighlights } from '../on-shown-auto-definition-highlights';
import { KeyPhraseCategory } from './key-phrase-category';
import type { KnowledgeDiscoveryKeyPhraseInputTextFormat } from './knowledge-discovery-key-phrase-input-text-format';
type JiraInputText = { text?: string; format: KnowledgeDiscoveryKeyPhraseInputTextFormat } | null;

type TenantArgs = {
	entityAri: string;
	workspaceId: string;
	product: string;
	inputText?: JiraInputText;
	fieldType?: string;
};

type JiraProductArgs = {
	jiraReporterAccountId?: string;
	jiraAssigneeAccountId?: string;
};
type AutoHighlightSupplierArgsType = TenantArgs & {
	productConfig?: JiraProductArgs;
	cloudId: string;
};

export type KeyPhraseNodeType = {
	keyPhrase: string;
	category: KeyPhraseCategory;
};

type AutohighlightSupplierState = {
	keyPhrases: KeyPhraseNodeType[];
	keyPhraseCategory: KeyPhraseCategory;
	autoHighlightRegex: RegExp;
	error: Error | undefined;
	loadAutohighlights: ({
		inputText,
	}: {
		inputText?: JiraInputText;
	}) => Promise<KeyPhraseNodeType[]>;
	resetAutohighlights: () => void;
	autohighlightsLoading: boolean;
};

export type KeyPhrasesQueryVariables = Omit<TenantArgs & JiraProductArgs, 'product'>;

export interface KeyPhrasesQueryType {
	knowledgeDiscovery: {
		keyPhrases: {
			nodes: KeyPhraseNodeType[];
		};
	};
}
export const AUTOHIGHLIGHTS_CACHE_KEY = 'atlassian.knowledge-discovery.autohighlights';

const escapeRegExp = (regex: string) => {
	return regex.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export const getHighlightRegex: (keyPhrases: KeyPhraseNodeType[]) => RegExp = (
	keyPhrases: KeyPhraseNodeType[],
): RegExp => {
	try {
		if (!keyPhrases.length) {
			return new RegExp('(?!)');
		} else {
			return new RegExp(
				keyPhrases.map((node) => `\\b${escapeRegExp(node.keyPhrase)}\\b`).join('|'),
				'gi',
			);
		}
	} catch {
		return new RegExp('(?!)');
	}
};

const getKeyphraseCategory = (keyPhrases: KeyPhraseNodeType[]): KeyPhraseCategory => {
	return keyPhrases.length ? keyPhrases[0].category : KeyPhraseCategory.AUTO;
};

const initialState = {
	keyPhrases: [],
	keyPhraseCategory: KeyPhraseCategory.AUTO,
	autoHighlightRegex: new RegExp('(?!)'),
	loadAutohighlights: () => Promise.resolve([]),
	resetAutohighlights: () => {},
	autohighlightsLoading: false,
	error: undefined,
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const useAutohighlightSupplier: ({
	entityAri,
	workspaceId,
	product,
	productConfig,
	fieldType,
	cloudId,
}: AutoHighlightSupplierArgsType) => {
	keyPhrases: KeyPhraseNodeType[];
	keyPhraseCategory: KeyPhraseCategory;
	autoHighlightRegex: RegExp;
	loadAutohighlights: ({
		inputText,
	}: {
		inputText?: JiraInputText;
	}) => Promise<KeyPhraseNodeType[]>;
	resetAutohighlights: () => void;
	autohighlightsLoading: boolean;
	error: Error | undefined;
} = ({
	entityAri,
	workspaceId,
	product,
	productConfig,
	fieldType,
	cloudId,
}: AutoHighlightSupplierArgsType) => {
	const [state, setState] = useState<AutohighlightSupplierState>(initialState);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			createAnalyticsEvent(payload).fire(DEFAULT_GAS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const { jiraReporterAccountId, jiraAssigneeAccountId } = productConfig || {};

	const loadAutohighlights = useCallback(
		async ({ inputText }: { inputText?: JiraInputText }) => {
			try {
				setState({
					...initialState,
					autohighlightsLoading: true,
				});

				const keyPhrasesResult = await fetchAggResponse<
					KeyPhrasesQueryVariables,
					KeyPhrasesQueryType
				>({
					graphQuery: KeyPhrasesQuery,
					variables: {
						workspaceId,
						entityAri,
						...(!!jiraReporterAccountId && { jiraReporterAccountId }),
						...(!!jiraAssigneeAccountId && { jiraAssigneeAccountId }),
						...(!!inputText && { inputText }),
					},
					operationName: KEY_PHRASES_OPERATION_NAME,
					headers: createAtlAttributionHeader({
						atlWorkspaceId: workspaceId,
						tenantId: cloudId,
						product,
					}),
				});
				const keyPhrases = keyPhrasesResult?.data?.knowledgeDiscovery?.keyPhrases?.nodes
					? keyPhrasesResult.data.knowledgeDiscovery.keyPhrases.nodes
					: [];

				if (keyPhrases.length > 0 && fg('kd_definitions_click_and_show_event_product_parity')) {
					fireAnalyticsEvent(
						onShownAutoDefinitionHighlights({
							product,
							contentId: entityAri,
							keyPhrases,
							fieldType,
						}),
					);
				}

				const autoHighlightRegex = getHighlightRegex(keyPhrases);
				setState({
					...initialState,
					keyPhraseCategory: getKeyphraseCategory(keyPhrases),
					autoHighlightRegex,
					keyPhrases,
				});

				return keyPhrases;
			} catch (error: any) {
				setState({
					...initialState,
					error: error.message,
				});
				return [];
			}
		},
		[
			entityAri,
			workspaceId,
			fireAnalyticsEvent,
			product,
			jiraReporterAccountId,
			jiraAssigneeAccountId,
			fieldType,
			cloudId,
		],
	);

	const resetAutohighlights = useCallback((): void => {
		setState(initialState);
	}, []);

	const autoHighlightRegex = useMemo(() => state.autoHighlightRegex, [state.autoHighlightRegex]);

	const autohighlightsLoading = useMemo(
		() => state.autohighlightsLoading,
		[state.autohighlightsLoading],
	);

	const keyPhrases = useMemo(() => state.keyPhrases, [state.keyPhrases]);

	const error = useMemo(() => state.error, [state.error]);

	return {
		keyPhrases,
		keyPhraseCategory: state.keyPhraseCategory,
		autoHighlightRegex,
		loadAutohighlights,
		resetAutohighlights,
		autohighlightsLoading,
		error,
	};
};
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { KnowledgeDiscoveryKeyPhraseInputTextFormat } from './knowledge-discovery-key-phrase-input-text-format';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { KeyPhraseCategory } from './key-phrase-category';
